import React from "react";

import { MoneticsFormProps, MoneticsFormState } from "./types";

/**
 * Carries the form model (`form` = hook return) and the component `props` down to
 * every field / brand-config component, replacing the previous prop drilling.
 *
 * Only the shared, multi-reader form state lives here. Component-local concerns
 * (modal open/close, in-flight fetches) stay in their owning component.
 */
export type MoneticsFormContextValue = {
    form: MoneticsFormState;
    props: MoneticsFormProps;
};

const MoneticsFormContext = React.createContext<MoneticsFormContextValue | null>(
    null
);

type MoneticsFormProviderProps = {
    value: MoneticsFormContextValue;
    children: React.ReactNode;
};

export function MoneticsFormProvider({
    value,
    children,
}: MoneticsFormProviderProps) {
    return (
        <MoneticsFormContext.Provider value={value}>
            {children}
        </MoneticsFormContext.Provider>
    );
}

/** Access the Monetics form model + props. Throws if used outside the provider. */
export function useMoneticsForm(): MoneticsFormContextValue {
    const context = React.useContext(MoneticsFormContext);
    if (context === null) {
        throw new Error(
            "useMoneticsForm must be used within a MoneticsFormProvider"
        );
    }
    return context;
}
